import type { MapDispatchToProps, MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { secondaryMarketOrderBlotterFilterByOrderId } from 'state/blotters/secondaryMarketOrderBlotter';
import { cpOrderAmendSelectors } from 'state/secondaryMarket/cpOrder/amend/amendedOrder.selector';
import {
  secondaryMarketCpOrderAmendRequestedThunk,
  secondaryMarketCpOrderAmendResetTabThunk,
} from 'state/secondaryMarket/cpOrder/amend/amendedOrder.thunks';
import {
  makeGetAmendCpOrderValidationErrors,
  makeGetBackendReports,
  makeGetModifiedFields,
} from 'state/secondaryMarket/cpOrder/cpOrderForm/cpOrderForm.selectors';
import { secondaryMarketCpOrderFormFieldChanged } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.actions';
import type { SecondaryMarketCpOrderForm } from 'state/secondaryMarket/cpOrder/cpOrderForm/input/cpOrderInputForm.model';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';
import { fromSecondaryMarket, getUserFrontRights } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryMarketPostEventTabType } from 'state/workspaces/model';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import type { LoadingStatus } from 'utils/status';

type OwnProps = Readonly<{
  tabId: string;
  tabType: SecondaryMarketPostEventTabType;
}>;

type StateProps = Readonly<{
  isLoading: boolean;
  canApply: boolean;
  kycScore: KycScoreStatus | undefined;
  kycDetails: readonly KycDetail[] | undefined;
  comment: string | undefined;
  applyStatus: LoadingStatus;
  validationErrors: readonly SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
  backendReports: readonly SecondaryMarketFormError<SecondaryMarketCpOrderForm>[];
  errorMessage: string | undefined;
}>;

type DispatchProps = Readonly<{
  onApply: () => void;
  onDone: () => void;
  onReset: () => void;
  onCommentChanged: (value: string) => void;
  onNavigateToOrdersBlotter: () => void;
}>;

export type AmendSidePanelProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getModifiedFields = makeGetModifiedFields();
  const getFormErrors = makeGetAmendCpOrderValidationErrors(getModifiedFields);
  const getBackendReports = makeGetBackendReports();
  return (state, { tabId }) => {
    const isLoading = getTabLoadingStatus(state, tabId) === TabLoadingStatus.Loading;
    const canApply = getUserFrontRights(state).secondaryTradeEnabled;
    const kycScore = fromSecondaryMarket.cpOrder.referential.kyc.getKycStatusScore(state, tabId);
    const kycDetails = fromSecondaryMarket.cpOrder.referential.kyc.getKycStatusDetails(state, tabId);
    const comment = fromSecondaryMarket.cpOrder.form.getFormComment(state, tabId);
    const applyStatus = cpOrderAmendSelectors.getAmendedOrderStatus(state, tabId);
    const errorMessage = cpOrderAmendSelectors.getErrorMessage(state, tabId);
    const formErrors = getFormErrors(state, tabId);
    const backendErrors = cpOrderAmendSelectors.getOrderValidationErrors(state, tabId);

    return {
      canApply,
      kycScore,
      kycDetails,
      comment,
      isLoading,
      applyStatus,
      validationErrors: applyStatus === 'idle' ? formErrors : backendErrors,
      errorMessage,
      backendReports: getBackendReports(state, tabId),
    };
  };
};
const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = (dispatch: ThunkDispatch, { tabId }) => ({
  onApply: () => {
    dispatch(secondaryMarketCpOrderAmendRequestedThunk(tabId));
  },
  onCommentChanged: value => {
    dispatch(secondaryMarketCpOrderFormFieldChanged(tabId, 'comment', value));
  },
  onDone: () => {
    dispatch(secondaryMarketCpOrderAmendResetTabThunk(tabId));
  },
  onReset: () => {
    dispatch(secondaryMarketCpOrderAmendResetTabThunk(tabId));
  },
  onNavigateToOrdersBlotter: () => {
    dispatch(secondaryMarketOrderBlotterFilterByOrderId(tabId));
  },
});

export const connectAmendCpOrderSidePanel = connect(mapStateToProps, mapDispatchToProps);
